import React, { useContext, useEffect, useState, useMemo } from 'react';
import Grid from '@mui/material/Grid2';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { APIContext } from '../../../Contexts/APIContext';
import SurveyViewModel from '../../ViewModels/SurveyViewModel';
import SurvayTableRow from './SurveyTableRow';
import { Typography } from '@mui/material';
import SurveyDayRowViewModel from '../../ViewModels/SurveyDayRowViewModel';
import AdditionalInfo from '../../../Popups/AdditionalInfo';
import EditControl from '../EditControl';
import getTimeIntervals from '../../../Utilities/Constants/HourTimeIntervals';
import SurveyDaysAdditionalDetailsViewModel from '../../ViewModels/SurveyDaysAdditionalDetailsViewModel';

interface IProps {
    transportType: number | null;
    date: string;
    id: string;
    readOnly: boolean;
    setReadOnly: React.Dispatch<React.SetStateAction<boolean>>;
    comment?: string;
    additionalDetails: SurveyDaysAdditionalDetailsViewModel;
    showAllOgvColumns: boolean;
    setShowAllOgvColumns: React.Dispatch<React.SetStateAction<boolean>>;
}

const timeColumnSx = { width: '8rem', minWidth: '8rem', whiteSpace: 'nowrap' } as const;
const numericColumnSx = { width: '4.5rem', minWidth: '4.5rem', whiteSpace: 'nowrap' } as const;
const averageColumnSx = { width: '5.5rem', minWidth: '5.5rem', whiteSpace: 'nowrap' } as const;
const accumulationColumnSx = { width: '8rem', minWidth: '8rem', whiteSpace: 'nowrap' } as const;

const hasAnyValues = (arr: number[] | null) =>
    !!arr && arr.some(v => v !== null && v !== 0);

const hasOgvSplitData = (values: number[] | null) =>
    !!values && values.some(value => value != null && value !== -1);

const VehicleOccupancyHeader = React.memo(
    ({ transportType, show }: { transportType: number | null; show: boolean }) => {

        if (!show)
            return null;

        const cellCount = transportType == 1 ? 2 : 7;
        return (
            <>
                {Array.from({ length: cellCount }, (_, index) => (
                    <TableCell key={`arr-${index}`} sx={numericColumnSx}>{transportType != 1 ? index + 1 : `OGV ${index + 1}`}</TableCell>
                ))}
            </>
        );
    }
);


export default function SurveyTable({ transportType, date, id, readOnly, setReadOnly, comment, additionalDetails, showAllOgvColumns, setShowAllOgvColumns }: IProps) {
    const { post, showMessage, get } = useContext(APIContext);
    const [survey, setSurvey] = useState<SurveyViewModel>(new SurveyViewModel());
    const [surveyRows, SetSurveyRows] = useState<SurveyDayRowViewModel[]>([]);
    const [ratio, setRatio] = useState<number | null>(null);
    const [arrivalsOccupancy, setArrivalsOccupancy] = useState<number[]>([]);
    const [departuresOccupancy, setDeparturesOccupancy] = useState<number[]>([]);

    const landUse = id.split('-')[1];
    const TimeIntervals = getTimeIntervals(landUse);

    useEffect(() => {
        if (date.length === 0 || transportType === null || transportType === 30 || transportType === 29) {
            SetSurveyRows([]);
            return;
        }

        const controller = new AbortController();
        setSurvey(new SurveyViewModel());
        SetSurveyRows([]);
        getData(!readOnly, transportType, controller.signal);
        get('SurveyDays', `GetPeopleVehicleRatio?id=${id}&date=${date}`, setRatio, controller.signal);

        return () => controller.abort();
    }, [date, transportType, readOnly, id]);

    const hasAnyOcc = useMemo(() => {
        const hasData = transportType === 1 ? hasOgvSplitData : hasAnyValues;
        return surveyRows.some(row =>
            hasData(row.vehicleOccupancyArrivals) || hasData(row.vehicleOccupancyDepartures));
    }, [surveyRows, transportType]);

    useEffect(() => {
        if (transportType !== 1) {
            setShowAllOgvColumns(false);
            return;
        }

        setShowAllOgvColumns(hasAnyOcc);
    }, [transportType, hasAnyOcc]);

    const showOccupancyColumns = transportType === 1 ? showAllOgvColumns : hasAnyOcc;

    const getData = (isEdit: boolean, mode: number | null, signal?: AbortSignal) => {
        if (mode != null) {
            get('SurveyDays', `GetSurvey?id=${id}&date=${date}&vehicleType=${mode}&isEdit=${isEdit}`, (data) => {
                setSurvey(data);
                let runningAccumulation = data?.initialParking ?? 0;
                const tempData: SurveyDayRowViewModel[] = [];
                let startPeriod = '';
                let endPeriod = '';
                let arrSum = [0, 0, 0, 0, 0, 0, 0];
                let depSum = [0, 0, 0, 0, 0, 0, 0];

                for (var index = 0; index < (landUse == '02' ? 48 : 24); index++) {
                    const rowIndex = landUse == '02' ? index : index * 2;
                    const time = TimeIntervals[index];
                    const arrivals = data.arrivals[rowIndex];
                    const departures = data.departures[rowIndex];
                    const vehicleOccupancyArrival = data.vehicleOccupancyArrivals?.[rowIndex]?.split(' ').filter(str => str !== '').map(Number) || null;
                    const hasVehicleOccupancyArrival = vehicleOccupancyArrival != null && vehicleOccupancyArrival.some(value => value !== -1);
                    if (vehicleOccupancyArrival != null) {
                        for (var i = 0; i < 7; i++) {
                            arrSum[i] += vehicleOccupancyArrival[i] == -1 ? 0 : vehicleOccupancyArrival[i];
                        }
                    }
                    const vehicleOccupancyDeparture = data.vehicleOccupancyDepartures?.[rowIndex]?.split(' ').filter(str => str !== '').map(Number) || null;
                    const hasVehicleOccupancyDeparture = vehicleOccupancyDeparture != null && vehicleOccupancyDeparture.some(value => value !== -1);
                    if (vehicleOccupancyDeparture != null) {
                        for (var i = 0; i < 7; i++) {
                            depSum[i] += vehicleOccupancyDeparture[i] == -1 ? 0 : vehicleOccupancyDeparture[i];
                        }
                    }
                    if (arrivals || departures) {
                        runningAccumulation += (arrivals ?? 0) - (departures ?? 0);
                    }

                    const hasSurveyData = arrivals != null || departures != null || hasVehicleOccupancyArrival || hasVehicleOccupancyDeparture;

                    if (!hasSurveyData) {
                        if (startPeriod === '') {
                            startPeriod = time.split('-')[0] || '';
                        }
                        endPeriod = time.split('-')[1] || '';
                        const nextRowIndex = landUse == "02" ? rowIndex + 1 : rowIndex + 2;
                        const nextArrivals = data.arrivals[nextRowIndex];
                        const nextDepartures = data.departures[nextRowIndex];
                        const nextVehicleOccupancyArrival = data.vehicleOccupancyArrivals?.[nextRowIndex]?.split(' ').filter(str => str !== '').map(Number) || null;
                        const nextVehicleOccupancyDeparture = data.vehicleOccupancyDepartures?.[nextRowIndex]?.split(' ').filter(str => str !== '').map(Number) || null;
                        const nextHasSurveyData = nextArrivals != null
                            || nextDepartures != null
                            || (nextVehicleOccupancyArrival != null && nextVehicleOccupancyArrival.some(value => value !== -1))
                            || (nextVehicleOccupancyDeparture != null && nextVehicleOccupancyDeparture.some(value => value !== -1));

                        if (index == TimeIntervals.length - 1 || nextHasSurveyData) {
                            const row = new SurveyDayRowViewModel();
                            row.departures = departures;
                            row.time = `${startPeriod}-${endPeriod}`;
                            row.runningAccumulation = runningAccumulation;
                            row.hasBrackets = data.initialParking === 0;
                            row.isVehicleOccupancy = isVehicleOccupancy;
                            row.survey = data;
                            tempData.push(row);
                            startPeriod = '';
                        }
                    }
                    else {
                        const row = new SurveyDayRowViewModel();
                        row.time = time;
                        row.arrivals = arrivals;
                        row.departures = departures;
                        row.vehicleOccupancyArrivals = vehicleOccupancyArrival;
                        row.vehicleOccupancyDepartures = vehicleOccupancyDeparture;
                        row.runningAccumulation = runningAccumulation;
                        row.hasBrackets = data.initialParking === 0;
                        row.isVehicleOccupancy = isVehicleOccupancy;
                        row.survey = data;
                        tempData.push(row);
                        startPeriod = '';
                    }
                }
                SetSurveyRows(tempData);
                setArrivalsOccupancy(arrSum);
                setDeparturesOccupancy(depSum);
            }, signal);
        }
    }

    const submit = () => {
        const surveyToSave = {
            ...survey,
            comments: comment ?? '',
            additionalDetails,
            ...(transportType === 1 && !showAllOgvColumns
                ? {
                    vehicleOccupancyArrivals: Array(48).fill(null),
                    vehicleOccupancyDepartures: Array(48).fill(null),
                }
                : {}),
        };

        post('SurveyDays', 'UpdateSurveyDays', surveyToSave, () => {
            showMessage('success', 'Survey updated successfully')
            setReadOnly(true);
        });
    }

    const totalArrivals = useMemo(() => {
        if (survey && survey.arrivals) {
            const relevantIndexes = survey.arrivals
                .map((_, index) => index)
                .filter(index => landUse === '02' || index % 2 === 0);

            return relevantIndexes.reduce((total: number, index: number) => {
                total += (survey.arrivals[index] ?? 0);
                return total;
            }, 0);
        } else
            return 0;
    }, [survey, landUse]);

    const totalDepartures = useMemo(() => {
        if (survey && survey.departures) {
            const relevantIndexes = survey.departures
                .map((_, index) => index)
                .filter(index => landUse === '02' || index % 2 === 0);

            return relevantIndexes.reduce((total: number, index: number) => {
                total += (survey.departures[index] ?? 0);
                return total;
            }, 0);
        } else
            return 0;
    }, [survey, landUse]);

    const totalArrivalsAndDepartures = totalArrivals + totalDepartures;
    const isVehicleOccupancy = transportType === 4 || transportType === 1;

    const editableRows = useMemo(() => {
        let runningAccumulation = survey?.initialParking ?? 0;
        var landUse = id.split('-')[1];
        return TimeIntervals.map((time, index) => {
            const rowIndex = landUse == '02' ? index : index * 2;
            const arrivals = survey.arrivals[rowIndex];
            const departures = survey.departures[rowIndex];

            const vehicleOccupancyArrival = survey.vehicleOccupancyArrivals?.[rowIndex]?.split(' ').filter(str => str !== '').map(Number) || null;
            const vehicleOccupancyDeparture = survey.vehicleOccupancyDepartures?.[rowIndex]?.split(' ').filter(str => str !== '').map(Number) || null;

            if (arrivals || departures) {
                runningAccumulation += (arrivals ?? 0) - (departures ?? 0);
            }

            return (
                <SurvayTableRow
                    key={time + rowIndex}
                    rowIndex={rowIndex}
                    setValue={setSurvey}
                    readOnly={readOnly}
                    time={time}
                    arrivals={arrivals}
                    departures={departures}
                    currentAccumulation={runningAccumulation}
                    vehicleOccupancyArrivals={vehicleOccupancyArrival}
                    vehicleOccupancyDepartures={vehicleOccupancyDeparture}
                    hasBrackets={survey.initialParking === 0}
                    isVehicleOccupancy={isVehicleOccupancy}
                    survey={survey}
                    transportType={transportType}
                    hasAnyOcc={showOccupancyColumns}
                />
            );
        });
    }, [survey, TimeIntervals, readOnly, isVehicleOccupancy, showOccupancyColumns]);

    const readOnlyRows = useMemo(() => {
        if (!readOnly) {
            return [];
        }

        const rows: JSX.Element[] = [];
        let emptyGroupStart: typeof surveyRows[number] | null = null;
        let emptyGroupEndTime = '';

        const hasRealOccupancyValues = (values: number[] | null) =>
            !!values && values.some(value => value != null && value !== 0 && value !== -1);

        const isEmptyReadOnlyRow = (row: SurveyDayRowViewModel) => {
            if (transportType === 1 && showOccupancyColumns) {
                return !hasOgvSplitData(row.vehicleOccupancyArrivals) &&
                    !hasOgvSplitData(row.vehicleOccupancyDepartures);
            }

            return row.arrivals == null &&
                row.departures == null &&
                !hasRealOccupancyValues(row.vehicleOccupancyArrivals) &&
                !hasRealOccupancyValues(row.vehicleOccupancyDepartures);
        };

        const flushEmptyGroup = () => {
            if (!emptyGroupStart) {
                return;
            }

            rows.push(
                <SurvayTableRow
                    key={emptyGroupStart.time}
                    rowIndex={rows.length * 2}
                    setValue={() => { }}
                    readOnly={true}
                    time={`${emptyGroupStart.time.split('-')[0]}-${emptyGroupEndTime.split('-')[1] ?? emptyGroupEndTime}`}
                    arrivals={emptyGroupStart.arrivals}
                    departures={emptyGroupStart.departures}
                    currentAccumulation={emptyGroupStart.runningAccumulation}
                    vehicleOccupancyArrivals={emptyGroupStart.vehicleOccupancyArrivals}
                    vehicleOccupancyDepartures={emptyGroupStart.vehicleOccupancyDepartures}
                    hasBrackets={emptyGroupStart.hasBrackets}
                    isVehicleOccupancy={emptyGroupStart.isVehicleOccupancy}
                    survey={emptyGroupStart.survey!}
                    transportType={transportType}
                    hasAnyOcc={showOccupancyColumns}
                />
            );

            emptyGroupStart = null;
            emptyGroupEndTime = '';
        };

        surveyRows.forEach((row) => {
            if (isEmptyReadOnlyRow(row)) {
                if (!emptyGroupStart) {
                    emptyGroupStart = row;
                }
                emptyGroupEndTime = row.time;
                return;
            }

            flushEmptyGroup();
            rows.push(
                <SurvayTableRow
                    key={row.time}
                    rowIndex={rows.length * 2}
                    setValue={() => { }}
                    readOnly={true}
                    time={row.time}
                    arrivals={row.arrivals}
                    departures={row.departures}
                    currentAccumulation={row.runningAccumulation}
                    vehicleOccupancyArrivals={row.vehicleOccupancyArrivals}
                    vehicleOccupancyDepartures={row.vehicleOccupancyDepartures}
                    hasBrackets={row.hasBrackets}
                    isVehicleOccupancy={row.isVehicleOccupancy}
                    survey={row.survey!}
                    transportType={transportType}
                    hasAnyOcc={showOccupancyColumns}
                />
            );
        });

        flushEmptyGroup();
        return rows;
    }, [readOnly, surveyRows, transportType, showOccupancyColumns]);


    return (
        <Grid size={12} >
            {transportType !== -1 &&
                <EditControl readOnly={readOnly} setReadOnly={setReadOnly} submit={submit} />
            }
            {isVehicleOccupancy &&
                <Grid size={8} container>
                    <Grid size={12}>
                        <Typography variant="body2">This count consists of car occupants, light goods vehicle occupants, motorcycle riders and OGV occupants Taxi drivers and drivers of private vehicles picking up/dropping off passengers at the site are excluded from the count</Typography>
                    </Grid>
                    <Grid size={6}>
                        <AdditionalInfo type="pdf" colour="info" icon="file" pdfContainer="GeneralDocs" pdfFile="Vehicle Occupants" buttonLabel="Note on Car Sharing and Vehicle Occupants Inclusion" />
                    </Grid>
                </Grid>
            }
            {(transportType == 0 || transportType == -1) &&
                <Typography variant="body2">{`Total People to Total Vehicle Ratio (All time periods and directions): ${ratio?.toFixed(2)}`}<span><AdditionalInfo type="dialog" colour="warning" icon="help" dialogTitle="Note on Total People to Total Vehicles ratio" dialogContent="The Total People to Total Vehicles ratio displays the number of all people (all modes combined) inbound plus outbound, divided by the number of total vehicles inbound plus outbound, to provide a comparison for individual surveys, and an average comparison for selected surveys in a trip rate calculation. It is important to note that all people are included in the ratio, and not just vehicle occupants." /></span></Typography>
            }
            {survey && survey.initialParking === 0 &&
                <Typography variant="body2">Bracketed figures assume initial number at site to be 0</Typography>
            }
            <TableContainer sx={{ width: 'fit-content', maxWidth: '100%', overflowX: 'auto' }}>
                <Table size="small" sx={{ width: 'auto' }}>
                    <TableHead>
                        {isVehicleOccupancy && transportType != 1 &&
                            <TableRow>
                                <TableCell sx={timeColumnSx}></TableCell>
                                <TableCell colSpan={7}>Occupancy per Vehicle Arriving</TableCell>
                                <TableCell colSpan={2}></TableCell>
                                <TableCell colSpan={7}>Occupancy per Vehicle Departing</TableCell>
                                <TableCell colSpan={5}></TableCell>
                            </TableRow>
                        }
                        <TableRow>
                            <TableCell sx={timeColumnSx}>Time</TableCell>
                            <VehicleOccupancyHeader transportType={transportType} show={showOccupancyColumns} />
                            <TableCell sx={numericColumnSx}>{transportType != 1 ? `Arr - ${totalArrivals}` : `Arr Total - ${totalArrivals}`}</TableCell>
                            {isVehicleOccupancy && transportType != 1 &&
                                <TableCell sx={averageColumnSx}>Ave Per Veh</TableCell>
                            }
                            <VehicleOccupancyHeader transportType={transportType} show={showOccupancyColumns} />
                            <TableCell sx={numericColumnSx}>{transportType != 1 ? `Dep - ${totalDepartures}` : `Dep Total - ${totalDepartures}`}</TableCell>
                            {isVehicleOccupancy && transportType != 1 &&
                                <TableCell sx={averageColumnSx}>Ave Per Veh</TableCell>
                            }
                            <TableCell sx={numericColumnSx}>Total - {totalArrivalsAndDepartures}</TableCell>
                            {isVehicleOccupancy && transportType != 1 &&
                                <TableCell sx={averageColumnSx}>Ave Per Veh</TableCell>
                            }
                            <TableCell sx={accumulationColumnSx}>{transportType == 0 ? "Parking Accumulation" : "Accumulation"}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {readOnly ?
                            readOnlyRows
                            :
                            editableRows
                        }
                        {isVehicleOccupancy && transportType != 1 &&
                            <SurvayTableRow
                                rowIndex={-1}
                                setValue={() => { }}
                                readOnly={true}
                                time={"Total"}
                                arrivals={totalArrivals}
                                departures={totalDepartures}
                                currentAccumulation={0}
                                vehicleOccupancyArrivals={arrivalsOccupancy}
                                vehicleOccupancyDepartures={departuresOccupancy}
                                hasBrackets={false}
                                isVehicleOccupancy={isVehicleOccupancy}
                                survey={survey}
                                transportType={transportType}
                                hasAnyOcc={showOccupancyColumns}
                            />
                        }
                    </TableBody>
                </Table>
            </TableContainer>
        </Grid >
    );
}
